import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";
import { motion } from "framer-motion";

const faqData = [
  {
    question: "Qual a diferença entre conexão via QR Code e API Oficial Meta?",
    answer: "Com QR Code você conecta rapidamente, ideal para começar com baixo custo por mensagem. Com a API Oficial Meta você ganha templates aprovados, baixo risco de banimento (desde que sigam as políticas de mensagens da Meta) e 99.9% de estabilidade. Você escolhe a opção que faz mais sentido — ambas estão disponíveis nos nossos planos.",
  },
  {
    question: "É difícil conectar à API Oficial do WhatsApp?",
    answer: "Não. A conexão à WhatsApp Business API é feita em minutos com login incorporado da Meta. Nossa equipe acompanha a verificação do Facebook Business Manager e configura os templates de mensagem aprovados para você. Tudo pronto para conversas profissionais sem complicação.",
  },
  {
    question: "Posso assinar mensal ou só anual?",
    answer: "Oferecemos ambos. O plano mensal não tem fidelidade. O plano anual tem desconto e pode ser parcelado em até 12x no cartão. Você escolhe a periodicidade na seção de planos.",
  },
  {
    question: "O que são templates de mensagem e por que importam?",
    answer: "Templates são modelos de mensagem aprovados pela Meta que permitem iniciar conversas com clientes a qualquer momento, mesmo fora da janela de 24h. Criamos e gerenciamos seus templates para que o atendimento seja mais profissional e proativo (notificações, promoções, recuperação de carrinho, etc).",
  },
  {
    question: "Quantos atendentes e números posso ter?",
    answer: "Ilimitados. Adicione toda a equipe e conecte quantos números de WhatsApp precisar — sem pagar por assento ou por conexão. Preço fixo, time e canais ilimitados.",
  },
  {
    question: "Funciona para clínicas e profissionais de saúde?",
    answer: "Sim. A Zavvi é LGPD compliant e amplamente usada por clínicas médicas, odontológicas e psicológicas para agendamento, confirmação de consultas e atendimento pós-consulta via WhatsApp.",
  },
  {
    question: "Posso usar para imobiliárias?",
    answer: "Sim. Imobiliárias usam a Zavvi para qualificar leads, enviar fotos e vídeos de imóveis, agendar visitas e fazer follow-up automático de propostas — tudo centralizado em um só painel.",
  },
  {
    question: "A IA responde automaticamente ou preciso configurar tudo?",
    answer: "A IA (ChatGPT, Claude ou Gemini — sua escolha) responde automaticamente com base no contexto da conversa. Você define quando ela deve transferir para um humano. A configuração leva menos de 30 minutos.",
  },
  {
    question: "Tenho uma agência. Posso revender para meus clientes?",
    answer: "Sim! Entre em contato pelo WhatsApp para conhecer nosso programa de parceiros e comissões para agências.",
  },
  {
    question: "Quais canais posso integrar além do WhatsApp?",
    answer: "Instagram Direct, Facebook Messenger, Telegram, Webchat para o seu site e E-mail — tudo na mesma caixa de entrada unificada.",
  },
  {
    question: "Quais formas de pagamento são aceitas?",
    answer: "Cartão de crédito, PIX, boleto e transferência. Para o plano anual, parcelamento em até 12x no cartão.",
  },
  {
    question: "Como funciona o cancelamento?",
    answer: "No plano mensal você pode cancelar a qualquer momento. No anual, o cancelamento encerra a renovação. Em ambos os casos, seus dados ficam disponíveis por 30 dias para exportação após o encerramento.",
  },
];

const FAQSection = () => {
  return (
    <section id="faq" className="py-16 md:py-24 relative" aria-labelledby="faq-heading">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-surface-card/20 to-transparent" />

      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-10 md:mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 md:px-4 py-2 rounded-full glass-card mb-4 md:mb-6">
            <HelpCircle size={16} className="md:w-[18px] md:h-[18px] text-cyber-cyan" />
            <span className="text-xs md:text-sm text-muted-foreground">Tire suas dúvidas</span>
          </div>
          <h2 id="faq-heading" className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-4 md:mb-6">
            Perguntas{" "}
            <span className="text-gradient-zavvi">Frequentes</span>
          </h2>
        </motion.div>

        {/* FAQ Accordion */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto"
        >
          <Accordion type="single" collapsible className="space-y-3">
            {faqData.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="glass-card rounded-xl md:rounded-2xl border-0 overflow-hidden"
              >
                <AccordionTrigger className="px-4 md:px-6 py-4 md:py-5 hover:no-underline hover:bg-muted/50 text-left">
                  <span className="text-foreground font-medium text-sm md:text-base pr-4">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="px-4 md:px-6 pb-4 md:pb-5">
                  <p className="text-body-text text-xs md:text-sm leading-relaxed">
                    {faq.answer}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mt-10 md:mt-16 text-center"
        >
          <p className="text-muted-foreground text-sm mb-3 md:mb-4">
            Ainda tem dúvidas?
          </p>
          <a
            href="#contato"
            className="inline-flex items-center gap-2 text-cyber-cyan hover:text-cyber-cyan/80 font-medium transition-colors text-sm md:text-base"
          >
            Falar com um especialista →
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;
